import { Injectable } from '@angular/core';
import { generatePrivateKey } from 'viem/accounts'
import type { Hex } from 'viem';
import { saveTextFile } from './file-save';

const BURNER_KEY = 'burnerKey';
const IMPORTED_KEY = 'importedKey';
const PW_KEY = 'password';
const PK_HEX_RE = /^0x[0-9a-f]{64}$/;

@Injectable({
  providedIn: 'root'
})
export class KeyStorage {

  private encoder = new TextEncoder();
  private decoder = new TextDecoder();

  constructor() {}

  async getOrSetPw() {
    const storedPw = this.getItem(PW_KEY);
    if (storedPw) {
      return storedPw;
    }
    else {
      const newPw = crypto.randomUUID();
      localStorage.setItem(PW_KEY, newPw);
      return newPw;
    }
  }

  hasImportedKey(): boolean {
    return this.getItem(IMPORTED_KEY) !== null;
  }

  // Active key = imported when present, otherwise burner. Burner is generated
  // on first call if absent and is never overwritten by import, so it remains
  // a usable fallback after forgetImportedKey() or a corrupted imported slot.
  async getOrSetKey(password: string): Promise<Hex | null> {
    if (this.hasImportedKey()) {
      const stored = this.getItem(IMPORTED_KEY)!;
      const decoded = await this.decryptItem<Hex>(password, stored);
      if (decoded) return decoded;
      console.warn('[KeyStorage] imported key failed to decrypt — falling back to burner');
    }
    const storedBurner = this.getItem(BURNER_KEY);
    if (storedBurner) {
      return this.decryptItem<Hex>(password, storedBurner);
    }
    const newKey = generatePrivateKey();
    await this.setEncryptedItem(BURNER_KEY, newKey, password);
    return newKey;
  }

  async exportRawPrivateKey(): Promise<Hex | null> {
    const slot = this.hasImportedKey() ? IMPORTED_KEY : BURNER_KEY;
    const stored = this.getItem(slot);
    if (!stored) return null;

    const password = await this.getOrSetPw();
    return this.decryptItem<Hex>(password, stored);
  }

  normalizePrivateKey(input: string): Hex | null {
    const trimmed = input.trim();
    const body = trimmed.startsWith('0x') || trimmed.startsWith('0X')
      ? trimmed.slice(2)
      : trimmed;
    const candidate = ('0x' + body.toLowerCase()) as Hex;
    return PK_HEX_RE.test(candidate) ? candidate : null;
  }

  forgetImportedKey(): void {
    localStorage.removeItem(IMPORTED_KEY);
  }

  async downloadPrivateKey(): Promise<'saved' | 'copied' | 'cancelled' | 'none'> {
    const key = await this.exportRawPrivateKey();
    if (!key) return 'none';
    return saveTextFile('pk.txt', key);
  }

  async importRawPrivateKey(rawInput: string): Promise<Hex> {
    const normalized = this.normalizePrivateKey(rawInput);
    if (!normalized) throw new Error('Invalid private key format');
    const password = await this.getOrSetPw();
    await this.setEncryptedItem(IMPORTED_KEY, normalized, password);
    return normalized;
  }

  private async setEncryptedItem(slotKey: string, value: any, password: string): Promise<void> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encryptionKey = await this.deriveKey(password, salt);
    const encodedValue = this.encoder.encode(JSON.stringify(value));
    const encryptedValue = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      encryptionKey,
      encodedValue
    );
    const result = new Uint8Array(salt.length + iv.length + encryptedValue.byteLength);
    result.set(salt, 0);
    result.set(iv, salt.length);
    result.set(new Uint8Array(encryptedValue), salt.length + iv.length);
    localStorage.setItem(slotKey, this.arrayBufferToBase64(result.buffer));
  }

  private getItem(key: string): string | null {
    const storedValue = localStorage.getItem(key);
    if (!storedValue) return null;
    return storedValue;
  }

  private async decryptItem<T>(password: string, storedValue: string): Promise<T | null> {
    try {
      const encryptedData = this.base64ToArrayBuffer(storedValue);
      const salt = encryptedData.slice(0, 16);
      const iv = encryptedData.slice(16, 28);
      const data = encryptedData.slice(28);
      const encryptionKey = await this.deriveKey(password, salt);
      const decryptedValue = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        encryptionKey,
        data
      );
      const decodedValue = this.decoder.decode(decryptedValue);
      return JSON.parse(decodedValue);
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  private async deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
    const baseKey = await crypto.subtle.importKey(
      'raw',
      this.encoder.encode(password),
      'PBKDF2',
      false,
      ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt.buffer as ArrayBuffer,
        iterations: 100000,
        hash: 'SHA-256'
      },
      baseKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
  }

  private base64ToArrayBuffer(base64: string): Uint8Array {
    return Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  }
}